import { useApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { Scenario } from '@/stores/type/scenario.type'
import { computed, ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import type { CalculationProgressInfo } from '@/components/scenario-result/type/type'
import { useDialog } from '@haskoning/vue-basestore/dist/composables/dialog'
import { useSelectedItemMixin } from '@/stores/useSelectedItemMixin'

const { api } = useApi()

const baseUrl = API.CORE.SIMULATIONS.INDEX
const duplicateURL = API.CORE.SIMULATIONS.DUPLICATE
const bulkDuplicateUrl = API.CORE.SIMULATIONS.BULK_DUPLICATE
const exportExcelUrl = API.CORE.SIMULATIONS.EXPORT_EXCEL
const exportErrorURL = API.CORE.SIMULATIONS.EXPORT_ERROR
const getCeleryCalculateUrl = API.CORE.SIMULATIONS.CELERY_CALCULATE
const getCalculationProgressStepsUrl = API.CORE.SIMULATIONS.GET_CALCULATION_PROGRESS_STEPS
const getCalculationProgressUrl = API.CORE.SIMULATIONS.GET_CALCULATION_PROGRESS
const getArcgisLocationPlatformTokenUrl = API.CORE.SIMULATIONS.GET_ARCGIS_LOCATION_PLATFORM_TOKEN

export const useScenarioStore = defineStore('calculation', () => {
  const {
    showDialog: duplicateDialog,
    dialogItem: duplicateDialogItem,
    openDialog: openDuplicateDialog,
    closeDialog: closeDuplicateDialog
  } = useDialog()

  const authorizationStore = useAuthorizationStore()

  const canAddScenario = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_simulation']
    })
  })

  const canChangeScenario = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_simulation']
    })
  })

  const canDeleteScenario = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_simulation']
    })
  })

  const baseStore = useBaseStore<Scenario>({ service: baseUrl, api })
  const selectedItemMixin = useSelectedItemMixin(baseStore)

  const isApprovedProjectParams = ref(true)
  const exportLoading = ref(false)
  const errorLoading = ref(false)

  const filterFields = ref(['name', 'location', 'client', 'description'])

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  const duplicate = async (item: Scenario) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api.service(duplicateURL(item.id)).post(item)
      if (Array.isArray(responseData)) {
        baseStore.addItems(responseData)
      } else {
        baseStore.addItems([responseData])
      }
      return responseData
    } catch (e) {
      baseStore.setSnackbarMessage('Failed to duplicate scenario', 'error')
      console.error(e)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const bulkDuplicate = async (items: Scenario[]) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api.service(bulkDuplicateUrl).post(items)
      if (Array.isArray(responseData) && responseData.length > 0) {
        baseStore.addItems(responseData)
      }
    } catch (e) {
      baseStore.setSnackbarMessage('Failed to duplicate calculations', 'error')
      console.error(e)
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const createNewScenario = (project: number, arcgisToken: string) => {
    const newScenario = {} as Scenario

    baseStore.fieldsList.value.forEach(({ parameter, children, default: defaultValue }) => {
      if (parameter === 'project') {
        newScenario[parameter] = project
      } else if (children) {
        newScenario[parameter] = Object.fromEntries(
          Object.entries(children).map(([subField, subFieldData]) => [
            subField,
            subFieldData.default
          ])
        )
      } else {
        newScenario[parameter] = defaultValue
      }
    })
    newScenario.arcgis_online_token = arcgisToken
    return newScenario
  }

  const celeryCalculate = async (item: Scenario, arcgisToken: string) => {
    try {
      return await api.service(getCeleryCalculateUrl(item.id)).post({
        ...item,
        arcgis_online_token: arcgisToken
      })
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const getScenarioProgressSteps = async (id: number): Promise<[]> => {
    try {
      return await api.service(getCalculationProgressStepsUrl(id)).get()
    } catch (e) {
      console.error(e)
      return []
    }
  }

  const getScenarioProgress = async (id: number): Promise<CalculationProgressInfo> => {
    try {
      return await api.service(getCalculationProgressUrl(id)).get()
    } catch (e) {
      console.error(e)
      return { state: 'unknown' }
    }
  }

  const getArcgisLocationPlatformToken = async (id: number): Promise<string | null> => {
    try {
      const response = await api.service(getArcgisLocationPlatformTokenUrl(id)).get()
      return response.arcgis_location_platform_token || null
    } catch (e) {
      console.error('Failed to retrieve ArcGIS Location Platform token:', e)
      return null
    }
  }

  const moveSelectedItemsToArchive = () => {
    console.log('move-selected-items-to-archive')
  }

  const duplicateSelectedItems = () => {
    openDuplicateDialog()
  }

  const deleteSelectedItems = () => {
    baseStore.openDeleteDialog()
  }

  const toggleDuplicateDialog = (value: boolean | undefined): void => {
    duplicateDialog.value = value ?? !duplicateDialog.value
  }

  const downloadExcel = async (id: number) => {
    exportLoading.value = true
    try {
      await api.service(exportExcelUrl(id)).downloadBlob()
    } catch (e) {
      console.error(e)
    }
    exportLoading.value = false
  }

  const toggleIsApprovedProjectParams = () => {
    isApprovedProjectParams.value = !isApprovedProjectParams.value
  }

  const downloadErrorFile = async (id: number) => {
    errorLoading.value = true
    try {
      await api.service(exportErrorURL(id)).downloadBlob()
    } catch (e) {
      console.error(e)
    }
    errorLoading.value = false
  }

  const updateFile = async (id: number, formData: FormData) => {
    try {
      const response = await api.service(baseUrl).patch(id, formData)
      const index = baseStore.items.value.findIndex((item) => item.id === response.id)
      if (index !== -1) {
        baseStore.items.value[index] = response
      }
      baseStore.setSnackbarMessage('File updated successfully', 'success')
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to update file', 'error')
      throw error
    }
  }

  const updateScenario = async (
    id: number,
    updatedFields: Partial<Scenario> = {},
    fileFieldsToExclude: string[] = []
  ) => {
    try {
      baseStore.writeLoading.value = true
      const nonFileFields = Object.fromEntries(
        Object.entries(updatedFields || {}).filter(([key]) => !fileFieldsToExclude.includes(key))
      )

      const response = await api.service(baseUrl).patch(id, nonFileFields)

      const index = baseStore.items.value.findIndex((item) => item.id === response.id)
      if (index !== -1) {
        baseStore.items.value[index] = response
      }

      baseStore.setSnackbarMessage('Scenario updated successfully', 'success')
    } catch (error) {
      console.error('Error updating scenario:', error)
      baseStore.setSnackbarMessage('Failed to update scenario', 'error')
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const getScenarioOptions = async () => {
    try {
      return await api.service(baseUrl).options()
    } catch (e) {
      console.error('Failed to fetch scenario OPTIONS', e)
      return null
    }
  }

  function resetAll() {
    baseStore.reset()
  }

  return {
    ...baseStore,
    ...selectedItemMixin,
    exportLoading,
    isApprovedProjectParams,
    errorLoading,
    duplicateDialog,
    duplicateDialogItem,
    openDuplicateDialog,
    closeDuplicateDialog,
    canAddScenario,
    canChangeScenario,
    canDeleteScenario,
    fieldsFiltered,
    createNewScenario,
    duplicate,
    bulkDuplicate,
    downloadExcel,
    downloadErrorFile,
    celeryCalculate,
    getScenarioProgressSteps,
    getScenarioProgress,
    toggleIsApprovedProjectParams,
    moveSelectedItemsToArchive,
    toggleDuplicateDialog,
    duplicateSelectedItems,
    deleteSelectedItems,
    updateFile,
    updateScenario,
    resetAll,
    getScenarioOptions,
    getArcgisLocationPlatformToken
  }
})
